import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import SignaturePad from 'signature_pad';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Eraser, User, FileText, CheckCircle, Share2, Loader2, X, Camera, ImagePlus, Trash2, AlertTriangle, IdCard } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { generateSalePDF } from '@/utils/pdfGenerator';
import { generatePdfFileName } from '@/utils/fileNameUtils';
import { captureException } from '@/lib/monitoring';
import { generateAndAttachCollectionTerm } from '@/utils/collectionTermPdf';
import { buildSignaturePath } from '@/lib/signatureStorage';
import { RentalDeliverySignatureDialog } from './RentalDeliverySignatureDialog';
import { deliverPdf } from '@/utils/deliverPdf';
import type { CollectionTermItem } from '@/components/Documents/Templates/CollectionTermDocument';
// L-LOC.9b FRONT-R: lista do que foi coletado de fato (D-LOC.31/37)
import { CollectionItemsChecklist } from '@/components/Rentals/CollectionItemsChecklist';
import {
  fetchRentalContractItems,
  fullSelection,
  summarizeSelection,
  buildItemsCollectedPayload,
  termItemsFromSelection,
  termItemsFromCollected,
  type CollectionSelection,
  type RentalContractItem,
} from '@/lib/rentalCollectionItems';

// Máscara de CPF
const formatCPF = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

// Converter base64 para blob
const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

// m-ASS.16 (FRONT-J3 M7): fetchRentalCollectionItems mudou para src/lib/rentalCollectionItems.ts.
// Reexportada aqui para não quebrar os importadores existentes.
import { fetchRentalCollectionItems } from '@/lib/rentalCollectionItems';
export { fetchRentalCollectionItems } from '@/lib/rentalCollectionItems';

type DialogStep = 'signing' | 'confirmed';

interface ConfirmedData {
  saleId: string | null;
  customerName: string;
  customerDocument: string;
  deliveryNumber: string;
  isCollection: boolean;
  signatureDataUrl: string;
  signatureDate: string;
  /** L-LOC.9b: coleta parcial — contrato segue ativo, assinatura anulada */
  isPartial?: boolean;
}

type PdfStep = 'idle' | 'loading_data' | 'generating' | 'sharing';

const PDF_STEP_LABEL: Record<PdfStep, string> = {
  idle:         'Compartilhar PDF',
  loading_data: 'Carregando dados...',
  generating:   'Gerando PDF...',
  sharing:      'Abrindo...',
};

interface DeliverySignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryId: string;
  deliveryNumber: string;
  onSuccess?: () => void;
  /** L-ASS.3 FRONT-J2: 'rental' delega para RentalDeliverySignatureDialog (entrega de locação). */
  originType?: string | null;
}

export const DeliverySignatureDialog = ({
  open,
  onOpenChange,
  deliveryId,
  deliveryNumber,
  onSuccess,
  originType,
}: DeliverySignatureDialogProps) => {
  const [customerName, setCustomerName] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string | null>(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [step, setStep] = useState<DialogStep>('signing');
  const [confirmedData, setConfirmedData] = useState<ConfirmedData | null>(null);
  const [pdfStep, setPdfStep] = useState<PdfStep>('idle');

  // §I.93 Ato 4 (H3.14) — termo de coleta entregue por botão Compartilhar,
  // no lugar do window.open automático; o blob já existe (gerado p/ o attach)
  const [collectionTermBlob, setCollectionTermBlob] = useState<Blob | null>(null);
  const [collectionTermFileName, setCollectionTermFileName] = useState('');
  const [collectionTermStatus, setCollectionTermStatus] = useState<'idle' | 'ready' | 'failed'>('idle');
  
  // Collection photo state
  const [collectionPhotoPreviews, setCollectionPhotoPreviews] = useState<string[]>([]);
  const [collectionPhotoFiles, setCollectionPhotoFiles] = useState<File[]>([]);
  const [deliveryType, setDeliveryType] = useState<string | null>(null);
  const [deliveryOriginType, setDeliveryOriginType] = useState<string | null>(null);
  const [rentalId, setRentalId] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Rental collection extras: document photo + damage
  const [documentPhotoFile, setDocumentPhotoFile] = useState<File | null>(null);
  const [documentPhotoPreview, setDocumentPhotoPreview] = useState<string | null>(null);
  const documentPhotoRef = useRef<HTMLInputElement>(null);
  const [hasDamage, setHasDamage] = useState(false);
  const [damageDescription, setDamageDescription] = useState('');
  const [damagePhotoFiles, setDamagePhotoFiles] = useState<File[]>([]);
  const [damagePhotoPreviews, setDamagePhotoPreviews] = useState<string[]>([]);
  const damagePhotoRef = useRef<HTMLInputElement>(null);

  // L-LOC.9b FRONT-R: itens do contrato + o que está sendo coletado de fato
  const [contractItems, setContractItems] = useState<RentalContractItem[]>([]);
  const [contractItemsLoading, setContractItemsLoading] = useState(false);
  const [collectionSelection, setCollectionSelection] = useState<CollectionSelection>({});

  const queryClient = useQueryClient();
  const { company, user: authUser } = useAuth();
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  const isCollection = deliveryType === 'collection';
  // Coleta de locação: usa a RPC atômica confirm_rental_collection_with_signature
  const isRentalCollection = isCollection || deliveryOriginType === 'rental_collection';
  
  const handleDocumentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCustomerDocument(formatted);
  }, []);

  // Fetch delivery type + origin_type + rental_id on open
  useEffect(() => {
    if (open && deliveryId) {
      supabase
        .from('deliveries')
        .select('type, origin_type, rental_id')
        .eq('id', deliveryId)
        .maybeSingle()
        .then(({ data }) => {
          setDeliveryType(data?.type || 'delivery');
          setDeliveryOriginType(data?.origin_type || null);
          setRentalId((data as any)?.rental_id || null);
        });
    }
  }, [open, deliveryId]);

  // L-LOC.9b: coleta de locação carrega os itens reais do contrato (com id de linha)
  // e nasce com tudo marcado — coleta total é o caso comum.
  useEffect(() => {
    if (!open || !isRentalCollection || !rentalId || !company?.id) return;
    let cancelled = false;
    (async () => {
      setContractItemsLoading(true);
      try {
        const items = await fetchRentalContractItems(rentalId, company.id);
        if (!cancelled) {
          setContractItems(items);
          setCollectionSelection(fullSelection(items));
        }
      } catch (err) {
        console.error('Erro ao carregar itens do contrato:', err);
        if (!cancelled) {
          setContractItems([]);
          setCollectionSelection({});
        }
      } finally {
        if (!cancelled) setContractItemsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isRentalCollection, rentalId, company?.id]);

  // Função para redimensionar o canvas corretamente (mobile + retina)
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signaturePadRef.current) return;
    
    const signatureData = signaturePadRef.current.isEmpty() 
      ? null 
      : signaturePadRef.current.toData();
    
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }
    
    signaturePadRef.current.clear();
    
    if (signatureData) {
      signaturePadRef.current.fromData(signatureData);
    }
  }, []);

  // Callback ref para detectar quando o canvas é montado
  const setCanvasCallback = useCallback((canvas: HTMLCanvasElement | null) => {
    canvasRef.current = canvas;
    if (canvas && open) {
      setCanvasReady(true);
    }
  }, [open]);

  // Reset quando o dialog fecha
  useEffect(() => {
    if (!open) {
      setCanvasReady(false);
      setStep('signing');
      setConfirmedData(null);
      setPdfStep('idle');
      setCollectionTermBlob(null);
      setCollectionTermFileName('');
      setCollectionTermStatus('idle');
      setCustomerName('');
      setCustomerDocument('');
      setCollectionPhotoPreviews([]);
      setCollectionPhotoFiles([]);
      setDeliveryType(null);
      setDeliveryOriginType(null);
      setRentalId(null);
      setDocumentPhotoFile(null);
      setDocumentPhotoPreview(null);
      setHasDamage(false);
      setDamageDescription('');
      setDamagePhotoFiles([]);
      setDamagePhotoPreviews([]);
      setContractItems([]);
      setContractItemsLoading(false);
      setCollectionSelection({});
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    }
  }, [open]);

  // Listener de resize
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [open, resizeCanvas]);

  // Inicializa SignaturePad quando canvas está pronto
  useEffect(() => {
    if (canvasReady && canvasRef.current && open) {
      const canvas = canvasRef.current;
      
      const timer = setTimeout(() => {
        try {
          if (signaturePadRef.current) {
            signaturePadRef.current.off();
          }
          
          signaturePadRef.current = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5,
          });
          
          resizeCanvas();
        } catch (error) {
          console.error('Error creating SignaturePad:', error);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [canvasReady, open, resizeCanvas]);
  
  const handleClear = () => {
    signaturePadRef.current?.clear();
  };

  // Photo handling for collections
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newFiles = [...collectionPhotoFiles, ...files];
    setCollectionPhotoFiles(newFiles);

    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCollectionPhotoPreviews(prev => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    setCollectionPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    setCollectionPhotoFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload collection photos to storage
  const uploadCollectionPhotos = async (): Promise<string[]> => {
    const urls: string[] = [];
    
    for (const file of collectionPhotoFiles) {
      // SEG-A2: pasta da empresa no bucket privado
      if (!company?.id) {
        console.warn('Erro ao fazer upload da foto: empresa não identificada');
        continue;
      }
      const fileName = buildSignaturePath(company.id, deliveryId, `collection-${Date.now()}-${Math.random().toString(36).substring(7)}.${file.name.split('.').pop() || 'jpg'}`);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false,
        });
      
      if (uploadError) {
        console.warn('Erro ao fazer upload da foto:', uploadError);
        continue;
      }
      
      const { data: urlData } = supabase.storage
        .from('signatures')
        .getPublicUrl(uploadData.path);
      
      urls.push(urlData.publicUrl);
    }
    
    return urls;
  };

  // Upload da foto do documento do signatário
  const uploadDocumentPhoto = async (): Promise<string | null> => {
    if (!documentPhotoFile) return null;
    if (!company?.id) { console.warn('Erro ao fazer upload do documento: empresa não identificada'); return null; }
    const ext = documentPhotoFile.name.split('.').pop() || 'jpg';
    const fileName = buildSignaturePath(company.id, deliveryId, `document_${Date.now()}.${ext}`);
    const { data: uploadData, error } = await supabase.storage
      .from('signatures')
      .upload(fileName, documentPhotoFile, { contentType: documentPhotoFile.type, upsert: false });
    if (error) { console.warn('Erro ao fazer upload do documento:', error); return null; }
    const { data: urlData } = supabase.storage.from('signatures').getPublicUrl(uploadData.path);
    return urlData.publicUrl;
  };

  // Upload das fotos de avaria
  const uploadDamagePhotos = async (): Promise<string[]> => {
    const urls: string[] = [];
    for (let i = 0; i < damagePhotoFiles.length; i++) {
      const file = damagePhotoFiles[i];
      if (!company?.id) { console.warn('Erro ao fazer upload da foto de avaria: empresa não identificada'); continue; }
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = buildSignaturePath(company.id, deliveryId, `damage_${i}_${Date.now()}.${ext}`);
      const { data: uploadData, error } = await supabase.storage
        .from('signatures')
        .upload(fileName, file, { contentType: file.type, upsert: false });
      if (error) { console.warn('Erro ao fazer upload da foto de avaria:', error); continue; }
      const { data: urlData } = supabase.storage.from('signatures').getPublicUrl(uploadData.path);
      urls.push(urlData.publicUrl);
    }
    return urls;
  };

  // Handlers para foto do documento
  const handleDocumentPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocumentPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setDocumentPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    if (documentPhotoRef.current) documentPhotoRef.current.value = '';
  };

  // Handlers para fotos de avaria
  const handleDamagePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setDamagePhotoFiles(prev => [...prev, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setDamagePhotoPreviews(prev => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
    if (damagePhotoRef.current) damagePhotoRef.current.value = '';
  };

  const removeDamagePhoto = (index: number) => {
    setDamagePhotoPreviews(prev => prev.filter((_, i) => i !== index));
    setDamagePhotoFiles(prev => prev.filter((_, i) => i !== index));
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSharePDF = async () => {
    if (!confirmedData?.saleId || !company?.id) {
      toast.error('Venda não encontrada para gerar PDF');
      return;
    }

    try {
      // ── Etapa 1: buscar dados em paralelo (sem round-trip de companies) ──
      setPdfStep('loading_data');

      // Dados da empresa já disponíveis no contexto — sem round-trip ao banco
      const companyPdfData = {
        name: company.name,
        razao_social: company.razao_social,
        cnpj: company.cnpj,
        endereco: company.endereco,
        numero: company.numero,
        bairro: company.bairro,
        cidade: company.cidade,
        estado: company.estado,
        cep: company.cep,
        telefone: company.telefone,
        email_corporativo: company.email_corporativo,
        logo_url: company.logo_url,
      };

      // Queries em paralelo — sale + installments ao mesmo tempo
      const [saleResult, installmentsResult] = await Promise.all([
        supabase
          .from('sales')
          .select(`
            *,
            customer:customers(id, name, document_number, email, phone, city, state, address, neighborhood, zip_code),
            items:sale_items(
              id, quantity, unit_price, discount_percentage, discount_value, discount_type,
              subtotal, total, deleted_at, notes,
              product:products(name, sku)
            ),
            salesperson:profiles!sales_salesperson_id_fkey(user_id, full_name)
          `)
          .eq('id', confirmedData.saleId)
          .eq('company_id', company.id)
          .single(),

        supabase
          .from('sale_installments')
          .select('*, payment_method:payment_methods(name)')
          .eq('sale_id', confirmedData.saleId)
          .order('installment_number'),
      ]);

      const saleData = saleResult.data;
      const installments = installmentsResult.data;

      if (!saleData) {
        toast.error('Erro ao carregar dados da venda');
        return;
      }
      saleData.items = (saleData.items || []).filter((i: any) => !i.deleted_at);

      // Resolver payment_conditions UUID para nome legível (condicional)
      let resolvedPaymentConditions: string | undefined = saleData.payment_conditions ?? undefined;
      if (resolvedPaymentConditions && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(resolvedPaymentConditions)) {
        const { data: pcData } = await supabase
          .from('payment_conditions')
          .select('name')
          .eq('id', resolvedPaymentConditions)
          .maybeSingle();
        if (pcData?.name) resolvedPaymentConditions = pcData.name;
      }

      const salesperson = (saleData as any).salesperson;

      // ── Etapa 2: gerar PDF (signature_data já em memória — sem trafegar no SQL) ──
      setPdfStep('generating');

      const saleForPDF = {
        ...saleData,
        installments: installments ?? [],
        payment_conditions: resolvedPaymentConditions,
        // Usa base64 já em memória — evita trafegar 30-50KB na query SQL
        signature_data: confirmedData.signatureDataUrl ?? saleData.signature_data,
        signature_customer_name: confirmedData.customerName ?? saleData.signature_customer_name,
        signature_customer_document: confirmedData.customerDocument ?? saleData.signature_customer_document,
        signature_date: confirmedData.signatureDate ?? saleData.signature_date,
      };

      const pdfBlob = await generateSalePDF({
        sale: saleForPDF,
        company: companyPdfData,
        salespersonName: salesperson?.full_name ?? undefined,
      });

      const fileName = generatePdfFileName(
        saleData.customer?.name,
        'Venda',
        saleData.sale_number
      );

      // ── Etapa 3: compartilhar ──
      setPdfStep('sharing');

      if (navigator.share && navigator.canShare) {
        const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: fileName });
            toast.success('PDF compartilhado!');
            return;
          } catch (shareError: any) {
            if (shareError?.name === 'AbortError') {
              // Usuário cancelou o share — comportamento normal, silenciar
              return;
            }
            if (shareError?.name === 'NotAllowedError') {
              // Permissão negada — fallback silencioso para download
              downloadBlob(pdfBlob, fileName);
              return;
            }
            throw shareError;
          }
        }
      }

      // Fallback: download direto
      downloadBlob(pdfBlob, fileName);
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      captureException(error, { context: 'DeliverySignatureDialog.handleSharePDF', saleId: confirmedData?.saleId });
      toast.error('Erro ao gerar PDF');
    } finally {
      setPdfStep('idle');
    }
  };
  
  // §I.93 Ato 4 (H3.14): o blob do termo já foi gerado para o attach —
  // aqui é só entrega, no mesmo padrão do comprovante de venda (deliverPdf)
  const handleShareCollectionTerm = async () => {
    if (!collectionTermBlob) return;
    await deliverPdf(collectionTermBlob, collectionTermFileName || 'termo-coleta.pdf');
  };

  const handleConfirm = async () => {
    // L-ASS.3 FRONT-J2: entrega de locação nunca passa pelo caminho de venda
    if (originType === 'rental' || deliveryOriginType === 'rental') {
      toast.error('Entrega de locação: use a tela de entrega de locação.');
      return;
    }

    if (!signaturePadRef.current) {
      toast.error('Erro interno: SignaturePad não inicializado');
      return;
    }
    
    if (signaturePadRef.current.isEmpty()) {
      toast.error('Por favor, colete a assinatura');
      return;
    }
    
    if (!customerName.trim()) {
      toast.error('Preencha o nome do recebedor');
      return;
    }

    // Validação: coleta de locação exige nome + CPF + assinatura
    if (isRentalCollection && !customerDocument.trim()) {
      toast.error('Preencha o CPF do responsável pela devolução');
      return;
    }

    // Validação: se avaria ativa, descrição obrigatória
    if (isRentalCollection && hasDamage && !damageDescription.trim()) {
      toast.error('Descreva a avaria identificada');
      return;
    }

    // Validação: foto obrigatória para coletas de venda (não rental_collection)
    if (isCollection && !isRentalCollection && collectionPhotoFiles.length === 0) {
      toast.error('Envie pelo menos 1 foto da coleta');
      return;
    }

    // L-LOC.9b: com itens carregados, ao menos um precisa estar marcado
    if (isRentalCollection && contractItems.length > 0
        && summarizeSelection(contractItems, collectionSelection).isEmpty) {
      toast.error('Marque ao menos um item coletado');
      return;
    }
    
    setLoading(true);
    setLoadingStep('Salvando assinatura...');
    
    try {
      const signatureDataUrl = signaturePadRef.current.toDataURL();
      const signatureDateTime = new Date().toISOString();
      
      // Buscar dados da entrega (incluindo type, origin_type, rental_id e itens da coleta)
      const { data: deliveryData } = await supabase
        .from('deliveries')
        .select('sale_id, type, rental_id, origin_type, collection_items')
        .eq('id', deliveryId)
        .maybeSingle();

      const effectiveRentalId = (deliveryData as any)?.rental_id || rentalId;
      const isRentalCollectionDelivery =
        deliveryData?.type === 'collection' ||
        (deliveryData as any)?.origin_type === 'rental_collection';
      
      // Upload da assinatura para o Storage
      let signatureUrl: string | null = null;
      try {
        const blob = dataURLtoBlob(signatureDataUrl);
        // SEG-A2: pasta da empresa no bucket privado
        if (!company?.id) {
          console.warn('Erro ao fazer upload da assinatura: empresa não identificada');
        } else {
          const fileName = buildSignaturePath(company.id, deliveryId, `${Date.now()}.png`);

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('signatures')
            .upload(fileName, blob, {
              contentType: 'image/png',
              upsert: false,
            });

          if (uploadError) {
            console.warn('Erro ao fazer upload da assinatura:', uploadError);
          } else {
            const { data: urlData } = supabase.storage
              .from('signatures')
              .getPublicUrl(uploadData.path);
            signatureUrl = urlData.publicUrl;
          }
        }
      } catch (uploadErr) {
        console.warn('Erro no upload:', uploadErr);
      }

      // ── COLETA DE LOCAÇÃO — fluxo atômico via RPC ───────────────────────
      if (isRentalCollectionDelivery && effectiveRentalId) {
        // ── Snapshot dos itens do termo ANTES da RPC (fallback): na parcial os
        // itens do contrato viram só o que FICOU; o termo deve sair do que SAIU.
        // Fonte principal é o `collected` devolvido pela RPC (abaixo).
        const hasItemList = contractItems.length > 0;
        const snapshotItems: CollectionTermItem[] = hasItemList
          ? termItemsFromSelection(contractItems, collectionSelection)
          : await fetchRentalCollectionItems(
              effectiveRentalId,
              company?.id,
              (deliveryData as any)?.collection_items,
            );
        // L-LOC.9b: lista do que foi coletado de fato — sempre enviada quando há itens
        // (também na total), para o registro da entrega guardar exatamente o que saiu.
        const itemsCollectedPayload = hasItemList
          ? buildItemsCollectedPayload(contractItems, collectionSelection)
          : null;

        // Upload paralelo: foto do documento + fotos de avaria
        setLoadingStep('Enviando fotos...');
        const [documentPhotoUrl, uploadedDamageUrls] = await Promise.all([
          uploadDocumentPhoto(),
          hasDamage ? uploadDamagePhotos() : Promise.resolve([] as string[]),
        ]);

        setLoadingStep('Confirmando coleta...');
        const { data: rpcResult, error: rpcError } = await (supabase.rpc as any)(
          'confirm_rental_collection_with_signature',
          {
            p_delivery_id:               deliveryId,
            p_rental_id:                 effectiveRentalId,
            p_signature_data:            signatureDataUrl,
            p_signature_url:             signatureUrl ?? '',
            p_customer_name:             customerName,
            p_customer_document:         customerDocument,
            p_signer_document_photo_url: documentPhotoUrl ?? null,
            p_has_damage:                hasDamage,
            p_damage_description:        hasDamage ? damageDescription : null,
            p_damage_photo_urls:         hasDamage ? uploadedDamageUrls : null,
            p_items_collected:           itemsCollectedPayload,
          }
        );

        if (rpcError) throw rpcError;

        // L-LOC.9b: o banco diz se foi parcial e o que saiu de fato
        const resultIsPartial: boolean = !!(rpcResult as any)?.is_partial;
        const collectedFromRpc = termItemsFromCollected((rpcResult as any)?.collected);
        const collectionItems: CollectionTermItem[] =
          collectedFromRpc.length > 0 ? collectedFromRpc : snapshotItems;

        toast.success(
          rpcResult?.has_damage
            ? 'Coleta confirmada — avaria registrada e responsável notificado'
            : resultIsPartial
              ? 'Coleta parcial confirmada — o contrato segue ativo'
              : 'Coleta confirmada com sucesso!'
        );

        queryClient.invalidateQueries({ queryKey: ['deliveries'] });
        queryClient.invalidateQueries({ queryKey: ['rentals'] });
        queryClient.invalidateQueries({ queryKey: ['rental-returns'] });
        queryClient.invalidateQueries({ queryKey: ['rental-stock'] });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['rental-history-events'] });

        // ── TERMO DE COLETA (Fase 1): gerar PDF, anexar ao evento e exibir ──
        // A coleta já está confirmada — falha aqui NUNCA desfaz nem bloqueia.
        const collectionEventId: string | null = (rpcResult as any)?.event_id ?? null;
        setLoadingStep('Gerando termo de coleta...');
        try {
          if (collectionEventId && company) {
            // Snapshot de contrato/cliente para o termo (o dialog não os conhece)
            const { data: rentalRow } = await supabase
              .from('rentals')
              .select('rental_number, status, customer:customers(name, document_number, address, city, state)')
              .eq('id', effectiveRentalId)
              .eq('company_id', company.id)
              .maybeSingle();

            const rentalCustomer = (rentalRow as any)?.customer ?? null;
            const companyAddress = [company.endereco, company.numero, company.bairro]
              .filter(Boolean)
              .join(', ');

            const termResult = await generateAndAttachCollectionTerm({
              eventId: collectionEventId,
              companyId: company.id,
              rentalNumber: rentalRow?.rental_number ?? deliveryNumber,
              term: {
                rentalNumber: rentalRow?.rental_number ?? deliveryNumber,
                collectionDateTime: signatureDateTime,
                // L-LOC.9b: a resposta da RPC é a fonte; status 'active' pós-RPC é o fallback (D4)
                isPartial: resultIsPartial || rentalRow?.status === 'active',
                forced: false,
                hasDamage,
                damageDescription: hasDamage ? damageDescription : null,
                damagePhotos: hasDamage ? damagePhotoPreviews : [],
                items: collectionItems,
                customer: {
                  name: rentalCustomer?.name ?? customerName,
                  document: rentalCustomer?.document_number ?? customerDocument,
                  address: rentalCustomer?.address ?? null,
                  city: rentalCustomer?.city ?? null,
                  state: rentalCustomer?.state ?? null,
                },
                company: {
                  name: company.name,
                  cnpj: company.cnpj,
                  address: companyAddress,
                  city: company.cidade,
                  state: company.estado,
                  logo_url: company.logo_url,
                },
                signature: {
                  dataUrl: signatureDataUrl,
                  name: customerName,
                  document: customerDocument,
                  date: signatureDateTime,
                },
              },
            });

            if (termResult.pdfBlob) {
              setCollectionTermBlob(termResult.pdfBlob);
              setCollectionTermFileName(
                termResult.pdfPath?.split('/').pop() ?? `TERMO_COLETA_${deliveryNumber}.pdf`
              );
              setCollectionTermStatus('ready');
            } else {
              setCollectionTermStatus('failed');
            }

            if (!termResult.ok) {
              toast.warning('Coleta confirmada, mas houve falha ao anexar o termo de coleta.');
            }
          } else {
            setCollectionTermStatus('failed');
          }
        } catch (termErr) {
          setCollectionTermStatus('failed');
          captureException(termErr, { context: 'DeliverySignatureDialog.collectionTerm', deliveryId });
          toast.warning('Coleta confirmada, mas o termo de coleta não pôde ser gerado.');
        }

        setConfirmedData({
          saleId: null,
          customerName,
          customerDocument,
          deliveryNumber,
          isCollection: true,
          signatureDataUrl,
          signatureDate: signatureDateTime,
          isPartial: resultIsPartial,
        });
        setLoadingStep('Pronto!');
        signaturePadRef.current?.clear();
        setStep('confirmed');
        return;
      }

      // ── ENTREGA DE VENDA — fluxo existente preservado ───────────────────

      // Upload de fotos de coleta (se houver — coletas de venda normais)
      let photoUrls: string[] = [];
      if (isCollection && collectionPhotoFiles.length > 0) {
        setLoadingStep('Enviando fotos...');
        photoUrls = await uploadCollectionPhotos();
      }
      
      setLoadingStep('Atualizando entrega...');
      const updateData: Record<string, any> = {
        status: 'delivered',
        delivered_at: signatureDateTime,
        signature_data: signatureDataUrl,
        signature_url: signatureUrl,
        signature_customer_name: customerName,
        signature_customer_document: customerDocument,
        signature_date: signatureDateTime,
      };

      if (photoUrls.length > 0) {
        updateData.photo_urls = photoUrls;
      }

      const { error } = await supabase
        .from('deliveries')
        .update(updateData)
        .eq('id', deliveryId);
      
      if (error) throw error;
      
      // Se a entrega tem uma venda vinculada, atualizar a venda também
      if (deliveryData?.sale_id) {
        const { data: currentSale } = await supabase
          .from('sales')
          .select('status, bill_on_delivery')
          .eq('id', deliveryData.sale_id)
          .maybeSingle();
        
        if (currentSale && currentSale.status !== 'delivered') {
          const { error: saleError } = await supabase
            .from('sales')
            .update({
              signature_data: signatureDataUrl,
              signature_customer_name: customerName,
              signature_customer_document: customerDocument,
              signature_date: signatureDateTime,
              status: 'delivered',
            })
            .eq('id', deliveryData.sale_id);

          if (saleError) {
            console.warn('Erro ao atualizar assinatura na venda:', saleError);
          }
        } else if (currentSale) {
          const { error: saleError } = await supabase
            .from('sales')
            .update({
              signature_data: signatureDataUrl,
              signature_customer_name: customerName,
              signature_customer_document: customerDocument,
              signature_date: signatureDateTime,
            })
            .eq('id', deliveryData.sale_id);

          if (saleError) {
            console.warn('Erro ao atualizar assinatura na venda:', saleError);
          }
        }

        // === FATURAR NA ENTREGA ===
        if (currentSale?.bill_on_delivery) {
          setLoadingStep('Gerando faturamento...');
          const { error: billingError } = await supabase.rpc('activate_bill_on_delivery' as any, {
            p_sale_id: deliveryData.sale_id,
            p_delivery_date: signatureDateTime,
          });
          if (billingError) {
            console.error('Erro ao faturar na entrega:', billingError);
            toast.error('Entrega confirmada, mas houve erro ao gerar faturamento');
          }
        }
      }
      
      const wasCollection = deliveryData?.type === 'collection';
      toast.success(wasCollection ? 'Coleta confirmada com sucesso!' : 'Entrega confirmada com assinatura!');
      
      // Invalidar queries
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['unified-sales'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-receivable'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });

      if (wasCollection) {
        queryClient.invalidateQueries({ queryKey: ['rentals'] });
        queryClient.invalidateQueries({ queryKey: ['rental-returns'] });
        queryClient.invalidateQueries({ queryKey: ['rental-stock'] });
      }
      
      setConfirmedData({
        saleId: deliveryData?.sale_id || null,
        customerName: customerName,
        customerDocument: customerDocument,
        deliveryNumber: deliveryNumber,
        isCollection: wasCollection,
        signatureDataUrl: signatureDataUrl,
        signatureDate: signatureDateTime,
      });
      
      setLoadingStep('Pronto!');
      signaturePadRef.current?.clear();
      setStep('confirmed');
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      captureException(error, { context: 'DeliverySignatureDialog.handleConfirm', deliveryId });
      const errorMessage = error?.message || error?.code || 'Erro desconhecido';
      toast.error(`Erro ao confirmar: ${errorMessage}`);
    } finally {
      setLoading(false);
      setLoadingStep(null);
    }
  };

  const handleClose = () => {
    if (step === 'confirmed') {
      onSuccess?.();
    }
    onOpenChange(false);
  };

  // L-ASS.3 FRONT-J2: entrega de locação (origin_type = 'rental') tem diálogo próprio — depois de todos os hooks
  if (originType === 'rental' || deliveryOriginType === 'rental') {
    return (
      <RentalDeliverySignatureDialog
        open={open}
        onOpenChange={onOpenChange}
        deliveryId={deliveryId}
        deliveryNumber={deliveryNumber}
        onSuccess={onSuccess}
      />
    );
  }
  
  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (loading) return;
        if (!newOpen && step === 'confirmed') {
          handleClose();
        } else {
          onOpenChange(newOpen);
        }
      }}
    >
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        hideCloseButton={step === 'confirmed' || loading}
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
        onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
      >
        {step === 'signing' ? (
          <>
            <DialogHeader className="flex-shrink-0">
              {/* A-LOC.2: a coleta de locação nasce com type 'delivery' no banco; o que a identifica é a origem */}
              <DialogTitle>
                {isCollection || isRentalCollection
                  ? `Assinatura de Coleta - ${deliveryNumber}`
                  : `Assinatura de Entrega - ${deliveryNumber}`
                }
              </DialogTitle>
              <DialogDescription className="sr-only">
                {isCollection || isRentalCollection
                  ? 'Formulário para coletar assinatura digital e fotos da coleta'
                  : 'Formulário para coletar assinatura digital do recebedor da entrega'
                }
              </DialogDescription>
            </DialogHeader>
            
            {/* Container com scroll */}
            <div className="flex-1 overflow-y-auto space-y-4 px-1 min-h-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customer-name" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Nome do Recebedor *
                  </Label>
                  <Input
                    id="customer-name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Nome completo"
                    className="h-12 text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer-document" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {isRentalCollection ? 'CPF do responsável *' : 'CPF (opcional)'}
                  </Label>
                  <Input
                    id="customer-document"
                    value={customerDocument}
                    onChange={handleDocumentChange}
                    placeholder="000.000.000-00"
                    className="h-12 text-base font-mono"
                    maxLength={14}
                  />
                </div>
              </div>
              
              {/* ── Foto do documento — coleta de locação ── */}
              {isRentalCollection && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <IdCard className="h-4 w-4" />
                    Foto do documento (RG ou CNH) — opcional
                  </Label>
                  <input
                    ref={documentPhotoRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleDocumentPhotoSelect}
                    className="hidden"
                  />
                  {documentPhotoPreview ? (
                    <div className="relative w-32 h-24 rounded-lg overflow-hidden border border-border">
                      <img
                        src={documentPhotoPreview}
                        alt="Documento"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => { setDocumentPhotoFile(null); setDocumentPhotoPreview(null); }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => documentPhotoRef.current?.click()}
                      className="w-full h-10 gap-2"
                    >
                      <Camera className="h-4 w-4" />
                      Tirar foto / Selecionar documento
                    </Button>
                  )}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Assinatura Digital *</Label>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    onClick={handleClear}
                  >
                    <Eraser className="h-4 w-4 mr-2" />
                    Limpar
                  </Button>
                </div>
                <div className="border-2 border-dashed border-border rounded-lg p-4">
                  <canvas 
                    ref={setCanvasCallback}
                    className="w-full h-32 border border-border rounded bg-white cursor-crosshair touch-none"
                    style={{ touchAction: 'none' }}
                  />
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Desenhe a assinatura acima usando o mouse ou touch
                  </p>
                </div>
              </div>

              {/* ── L-LOC.9b: o que está sendo coletado — coleta de locação ── */}
              {isRentalCollection && (
                <div className="border border-border rounded-lg p-3">
                  <CollectionItemsChecklist
                    items={contractItems}
                    selection={collectionSelection}
                    onChange={setCollectionSelection}
                    loading={contractItemsLoading}
                    disabled={loading}
                  />
                </div>
              )}

              {/* ── Seção de avarias — coleta de locação ── */}
              {isRentalCollection && (
                <div className="space-y-3 border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      Identificou avarias no equipamento?
                    </Label>
                    <Switch
                      checked={hasDamage}
                      onCheckedChange={setHasDamage}
                    />
                  </div>
                  {hasDamage && (
                    <div className="space-y-3 pt-1">
                      <div className="space-y-1">
                        <Label htmlFor="damage-description">Descreva as avarias *</Label>
                        <Textarea
                          id="damage-description"
                          value={damageDescription}
                          onChange={(e) => setDamageDescription(e.target.value)}
                          placeholder="Ex.: cilindro com amassado no corpo, válvula com vazamento..."
                          className="min-h-[80px] text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Camera className="h-4 w-4" />
                          Fotos das avarias
                        </Label>
                        <input
                          ref={damagePhotoRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          multiple
                          onChange={handleDamagePhotoSelect}
                          className="hidden"
                        />
                        {damagePhotoPreviews.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {damagePhotoPreviews.map((preview, index) => (
                              <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                                <img src={preview} alt={`Avaria ${index + 1}`} className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeDamagePhoto(index)}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => damagePhotoRef.current?.click()}
                          className="w-full h-10 gap-2"
                        >
                          <ImagePlus className="h-4 w-4" />
                          {damagePhotoPreviews.length > 0
                            ? `Adicionar mais (${damagePhotoPreviews.length} foto${damagePhotoPreviews.length > 1 ? 's' : ''})`
                            : 'Tirar foto da avaria'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Seção de fotos - coletas de venda (não rental_collection) */}
              {isCollection && !isRentalCollection && (
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Fotos da Coleta *
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Registre o estado dos equipamentos coletados (mínimo 1 foto)
                  </p>
                  
                  {/* Photo previews */}
                  {collectionPhotoPreviews.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {collectionPhotoPreviews.map((preview, index) => (
                        <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                          <img 
                            src={preview} 
                            alt={`Foto ${index + 1}`} 
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full h-12 gap-2"
                  >
                    <ImagePlus className="h-5 w-5" />
                    {collectionPhotoPreviews.length > 0 
                      ? `Adicionar mais fotos (${collectionPhotoPreviews.length} selecionada${collectionPhotoPreviews.length > 1 ? 's' : ''})` 
                      : 'Tirar foto / Selecionar imagem'
                    }
                  </Button>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirm} 
                disabled={
                  loading ||
                  (isRentalCollection && hasDamage && !damageDescription.trim()) ||
                  (isRentalCollection && contractItemsLoading) ||
                  (isRentalCollection && contractItems.length > 0
                    && summarizeSelection(contractItems, collectionSelection).isEmpty)
                }
                className={isCollection || isRentalCollection ? 'bg-orange-600 hover:bg-orange-700' : ''}
              >
                {loadingStep 
                  ? loadingStep 
                  : isRentalCollection
                    ? 'Confirmar Coleta'
                    : isCollection 
                      ? 'Confirmar Coleta' 
                      : 'Confirmar Entrega'
                }
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {/* Tela de confirmação */}
            <DialogHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <DialogTitle className={`flex items-center gap-2 ${
                  confirmedData?.isCollection 
                    ? 'text-orange-600 dark:text-orange-400' 
                    : 'text-emerald-600 dark:text-emerald-400'
                }`}>
                  <CheckCircle className="h-5 w-5" />
                  {confirmedData?.isCollection ? 'Coleta Confirmada!' : 'Entrega Confirmada!'}
                </DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <DialogDescription className="sr-only">
                {confirmedData?.isCollection ? 'Coleta confirmada com sucesso' : 'Entrega confirmada com sucesso'}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 flex flex-col items-center justify-center py-8 space-y-6">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                confirmedData?.isCollection 
                  ? 'bg-orange-100 dark:bg-orange-900/30' 
                  : 'bg-emerald-100 dark:bg-emerald-900/30'
              }`}>
                <CheckCircle className={`h-10 w-10 ${
                  confirmedData?.isCollection 
                    ? 'text-orange-600 dark:text-orange-400' 
                    : 'text-emerald-600 dark:text-emerald-400'
                }`} />
              </div>

              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">{confirmedData?.deliveryNumber}</h3>
                <p className="text-muted-foreground">
                  Recebedor: <span className="font-medium text-foreground">{confirmedData?.customerName}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Assinatura registrada com sucesso
                </p>
                {confirmedData?.isCollection && !confirmedData.isPartial && (
                  <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                    Locação encerrada automaticamente
                  </p>
                )}
                {confirmedData?.isCollection && confirmedData.isPartial && (
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    Coleta parcial: o contrato segue ativo com o que ficou e a assinatura
                    vigente foi anulada. O gestor foi avisado para emitir e enviar o contrato novo.
                  </p>
                )}
              </div>

              {/* Botão de PDF apenas para entregas com venda */}
              {confirmedData?.saleId && !confirmedData?.isCollection && (
                <Button
                  size="lg"
                  onClick={handleSharePDF}
                  disabled={pdfStep !== 'idle'}
                  className="gap-2 min-w-[200px]"
                >
                  {pdfStep !== 'idle' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Share2 className="h-5 w-5" />
                  )}
                  {PDF_STEP_LABEL[pdfStep]}
                </Button>
              )}

              {/* H3.14: termo de coleta entregue por botão, não aba automática */}
              {confirmedData?.isCollection && collectionTermStatus === 'ready' && (
                <Button
                  size="lg"
                  onClick={handleShareCollectionTerm}
                  className="gap-2 min-w-[200px]"
                >
                  <Share2 className="h-5 w-5" />
                  Compartilhar Termo
                </Button>
              )}
              {confirmedData?.isCollection && collectionTermStatus === 'failed' && (
                <p className="text-sm text-muted-foreground text-center">
                  O termo de coleta não pôde ser gerado — a coleta permanece confirmada.
                </p>
              )}
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                className="w-full"
              >
                Fechar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
